import React, { useState } from 'react';
import { devLog } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import { useAdditionalInfoCheck } from '../../hooks/useAdditionalInfoCheck';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import PrimaryButton from '../../components/PrimaryButton';
import BottomNavigation from '../../customComponents/BottomNavigation';
import LoginRequiredModal from '../../components/LoginRequiredModal';
import LogoutConfirmModal from '../../components/LogoutConfirmModal';
import AdditionalInfoModal from '../../components/AdditionalInfoModal';

// MyPageContainer styles are now handled by PageContainerLayout

const LoginStatusContainer = styled.div<{ $isLoggedIn: boolean }>`
  /* height: 156px; */
  background-color: ${props => props.$isLoggedIn ? '#f3f3f3' : '#ffffff'};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: ${props => props.$isLoggedIn ? 'flex-start' : 'center'};
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  
  /* 모바일에서만 전체 너비 사용 */
  @media (max-width: 767px) {
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
  }
`;

const UserName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #262626;
  margin-bottom: 8px;
`;

const UserEmail = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #949494;
  margin-bottom: 16px;
`;

const EditProfileButtonContainer = styled.div`
  width: 100%;
  margin-top: auto;
`;

const NotLoggedInText = styled.div`
  font-size: 16px;
  color: #000000;
  font-weight: 400;
  text-align: left;
  cursor: pointer;
  
  span.login-text {
    font-size: 18px;
    font-weight: bold;
    color: #262626;
  }
  
  &:hover span.login-text {
    color: #0066cc;
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  height: 10px;
  background-color: #f3f3f3;
  width: 100%;
  
  /* 모바일에서만 전체 너비 사용 */
  @media (max-width: 767px) {
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
  }
`;

const SectionContainer = styled.div`
  background-color: #ffffff;
  padding: 20px 0;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  text-align: left;
`;

const MenuItem = styled.div`
  padding: 12px 0;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    color: #666;
  }
  
  &::after {
    content: '›';
    font-size: 20px;
    color: #666;
    font-weight: 300;
  }
`;

// 다국어 텍스트 객체
const texts = {
  ko: {
    myPage: '마이페이지',
    loginRequired: '후 이용 가능합니다',
    loginText: '로그인',
    editProfile: '정보 수정',
    payment: '결제',
    paymentManagement: '결제관리',
    paymentHistory: '결제이력',
    convenience: '편의',
    notificationSettings: '알림설정',
    terms: '약관보기',
    logout: '로그아웃',
    userSuffix: '님',
    noEmail: '이메일 없음',
    defaultUserName: '사용자'
  },
  en: {
    myPage: 'My Page',
    loginRequired: 'Available after login',
    loginText: 'Login',
    editProfile: 'Edit Profile',
    payment: 'Payment',
    paymentManagement: 'Payment Management',
    paymentHistory: 'Payment History',
    convenience: 'Convenience',
    notificationSettings: 'Notification Settings',
    terms: 'Terms',
    logout: 'Logout',
    userSuffix: '',
    noEmail: 'No Email',
    defaultUserName: 'User'
  },
};

function MyPage() {
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useUserAuth();
  const { lang } = useLang();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // 알림 설정 추가 정보 체크 훅
  const notificationSettingsInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/notification-settings')
  });

  const t = texts[lang];

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    logout();
    setShowLogoutModal(false);
    navigate('/login');
  };

  const handleCloseLogoutModal = () => {
    setShowLogoutModal(false);
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleLoginRequired = () => {
    setShowLoginModal(true);
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  // 로그인이 필요한 기능들에 대한 핸들러
  const handleLoginRequiredAction = (action: () => void) => {
    if (isLoggedIn) {
      action();
    } else {
      setShowLoginModal(true);
    }
  };

  // 추가 정보 체크가 필요한 기능들에 대한 핸들러
  const handleAdditionalInfoRequiredAction = (infoCheck: any) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // 로그인된 경우 추가 정보 체크 실행
    infoCheck.checkAndNavigate();
  };

  return (
    <>
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="mypage"
          title={t.myPage}
        />
        <PageContainerLayout mobilePaddingTop={0}>
          <LoginStatusContainer $isLoggedIn={isLoggedIn}>
            {isLoggedIn ? (
              <>
                <UserName>{user?.name || t.defaultUserName}<span style={{fontSize: '16px', fontWeight: 400, color: '#949494'}}>{t.userSuffix}</span></UserName>
                <UserEmail>{user?.email || t.noEmail}</UserEmail>
                <EditProfileButtonContainer>
                  <PrimaryButton onClick={handleEditProfile}>
                    {t.editProfile}
                  </PrimaryButton>
                </EditProfileButtonContainer>
              </>
            ) : (
              <NotLoggedInText onClick={() => navigate('/login')}>
                <span className="login-text">{t.loginText}</span> {t.loginRequired}
              </NotLoggedInText>
            )}
          </LoginStatusContainer>
          
          <SectionContainer>
            <SectionTitle>{t.payment}</SectionTitle>
            {user?.locationType === 'DOMESTIC' && (
              <MenuItem onClick={() => {/* 서버 개발 전까지 터치 이벤트 비활성화 */}}>
                {t.paymentManagement}
              </MenuItem>
            )}
            <MenuItem onClick={() => {/* 서버 개발 전까지 터치 이벤트 비활성화 */}}>
              {t.paymentHistory}
            </MenuItem>
          </SectionContainer>
          
          <Divider />
          
          <SectionContainer>
            <SectionTitle>{t.convenience}</SectionTitle>
            <MenuItem onClick={() => handleAdditionalInfoRequiredAction(notificationSettingsInfoCheck)}>
              {t.notificationSettings}
            </MenuItem>
            <MenuItem onClick={() => navigate('/terms')}>
              {t.terms}
            </MenuItem>
            {isLoggedIn && (
              <MenuItem onClick={handleLogout}>
                {t.logout}
              </MenuItem>
            )}
          </SectionContainer>
        </PageContainerLayout>
      </CenteredLayout>
      
      {/* 바텀 네비게이션 - 모바일에서만 표시 */}
      <BottomNavigation
        currentPage="mypage"
        onLoginRequired={handleLoginRequired}
      />
      
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginFromModal}
      />
      
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCloseLogoutModal}
        onConfirm={handleConfirmLogout}
        language={lang}
      />

      {/* 알림 설정 추가 정보 모달 */}
      <AdditionalInfoModal
        isOpen={notificationSettingsInfoCheck.showAdditionalInfoModal}
        onClose={notificationSettingsInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={notificationSettingsInfoCheck.handleGoToAdditionalInfo}
      />
    </>
  );
}

export default MyPage;
